import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { LogIn } from "lucide-react";

export default function Login() {
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();

  const [formData, setFormData] = useState({ email: "", password: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success(t('loginSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#EEF2F7] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#3B82F6]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#F97316]/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/40 rounded-full blur-3xl" />
      </div>

      {/* Language switcher top-right */}
      <div className="absolute top-4 right-4 z-20">
        <button
          data-testid="login-language-switcher"
          onClick={toggleLanguage}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-[#E2E8F0] shadow-sm hover:shadow-md transition-all text-sm font-semibold"
        >
          <span className={language === 'fr' ? 'text-[#1E293B]' : 'text-[#94A3B8]'}>FR</span>
          <span className="text-[#CBD5E1]">|</span>
          <span className={language === 'ro' ? 'text-[#1E293B]' : 'text-[#94A3B8]'}>RO</span>
        </button>
      </div>

      <Card className="w-full max-w-md bg-white border-[#E2E8F0] shadow-xl relative z-10 animate-fadeIn">
        <CardHeader className="text-center pb-4">
          <div className="w-24 h-24 mx-auto mb-4 rounded-2xl bg-white border-2 border-[#E2E8F0] flex items-center justify-center overflow-hidden shadow-lg animate-float">
            <img src="/logo.png" alt="VGI Construct" className="w-full h-full object-contain" />
          </div>
          <CardTitle className="text-3xl font-bold font-['Barlow_Condensed'] uppercase text-[#1E293B]">
            {t('welcomeTitle')}
          </CardTitle>
          <p className="text-[#64748B] flex items-center justify-center gap-2 mt-1 text-sm">
            <span className="text-[#F97316]">★</span>
            {t('loginSubtitle')}
          </p>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">{t('emailField')}</Label>
              <Input
                data-testid="input-email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">{t('passwordField')}</Label>
              <Input
                data-testid="input-password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg"
              />
            </div>

            <Button
              data-testid="btn-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#F97316] to-[#EA580C] hover:from-[#FB923C] hover:to-[#F97316] text-white rounded-xl h-12 font-semibold shadow-lg shadow-orange-200 transition-all duration-300"
            >
              {loading
                ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <><LogIn className="w-4 h-4 mr-2" />{t('loginBtn')}</>
              }
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
